/**
 * Calendar and Scheduling Utilities
 * Handles date math, frequency schedule parsing, time slot mapping, and regimen lookups.
 */

import { calculateEffectiveStatus } from './statusUtils.js';
import { normalizeCompoundCategory } from './sortUtils.js';
import {
  parseFrequencySchedule,
  isScheduleActiveOnDate,
  getProtocolAnchorDate,
  getDayDifference,
  extractDaysOfWeek,
  parseNumberToken
} from './frequencyParser.js';

export {
  parseFrequencySchedule,
  isScheduleActiveOnDate,
  getProtocolAnchorDate,
  getDayDifference,
  extractDaysOfWeek,
  parseNumberToken
};

export const TIME_SLOTS = [
  {
    id: 'AM',
    label: 'Morning / AM',
    shortLabel: 'AM',
    timeRange: 'Wake-up – 11:00 AM',
    color: 'amber',
    borderColor: 'border-amber-500/30',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-400',
    dotColor: 'bg-amber-400'
  },
  {
    id: 'NOON',
    label: 'Midday / Noon',
    shortLabel: 'Noon',
    timeRange: '11:00 AM – 4:00 PM',
    color: 'sky',
    borderColor: 'border-sky-500/30',
    bgColor: 'bg-sky-500/10',
    textColor: 'text-sky-400',
    dotColor: 'bg-sky-400'
  },
  {
    id: 'BEDTIME',
    label: 'Evening / Bedtime',
    shortLabel: 'Bedtime',
    timeRange: '4:00 PM – Sleep',
    color: 'indigo',
    borderColor: 'border-indigo-500/30',
    bgColor: 'bg-indigo-500/10',
    textColor: 'text-indigo-400',
    dotColor: 'bg-indigo-400'
  }
];

export const PRN_SLOT = {
  id: 'PRN',
  label: 'As Needed / PRN',
  shortLabel: 'PRN',
  timeRange: 'Flexible / Acute',
  color: 'emerald',
  borderColor: 'border-emerald-500/30',
  bgColor: 'bg-emerald-500/10',
  textColor: 'text-emerald-400',
  dotColor: 'bg-emerald-400'
};

/**
 * Format a Date object to local YYYY-MM-DD string without timezone distortion.
 */
export function formatDateIso(date) {
  if (!date) return '';
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Formats a Date object or ISO string into a human-readable title.
 * e.g. "Wednesday, October 7, 2026"
 */
export function formatFullDate(date) {
  const d = typeof date === 'string' ? parseDateIso(date) : date;
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Parse YYYY-MM-DD into a local Date object.
 */
export function parseDateIso(str) {
  if (!str) return new Date();
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Check if two dates are the same day (in local time).
 */
export function isSameDay(d1, d2) {
  if (!d1 || !d2) return false;
  const a = new Date(d1);
  const b = new Date(d2);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Maps protocol timingOfDay string into one or more time slots ('AM', 'NOON', 'BEDTIME', 'PRN').
 */
export function mapTimingToSlots(timingOfDay = '') {
  const timing = (timingOfDay || '').toLowerCase().trim();

  if (!timing) {
    return ['AM'];
  }

  // Handle explicit PRN / As Needed
  if (
    timing.includes('prn') || 
    timing.includes('as needed') || 
    timing.includes('flexible') || 
    timing.includes('workout')
  ) {
    return ['PRN'];
  }

  const slots = new Set();

  // Multi-dose patterns
  if (
    timing.includes('twice daily') || 
    timing.includes('am / evening') || 
    timing.includes('am & bedtime') || 
    timing.includes('am / bedtime') || 
    timing.includes('bid') ||
    timing.includes('morning & night') ||
    timing.includes('morning & bedtime')
  ) {
    slots.add('AM');
    slots.add('BEDTIME');
  }

  if (
    timing.includes('three times') || 
    timing.includes('tid') || 
    timing.includes('thrice')
  ) {
    slots.add('AM');
    slots.add('NOON');
    slots.add('BEDTIME');
  }

  // Morning / AM keywords
  if (
    timing.includes('am') || 
    timing.includes('morning') || 
    timing.includes('wake') || 
    timing.includes('fasted') ||
    timing.includes('breakfast')
  ) {
    slots.add('AM');
  }

  // Midday / Noon keywords
  if (
    timing.includes('noon') || 
    timing.includes('midday') || 
    timing.includes('afternoon') || 
    timing.includes('lunch')
  ) {
    slots.add('NOON');
  }

  // Evening / Bedtime keywords
  if (
    timing.includes('bedtime') || 
    timing.includes('evening') || 
    timing.includes('pm') || 
    timing.includes('night') || 
    timing.includes('dinner') ||
    timing.includes('sleep')
  ) {
    slots.add('BEDTIME');
  }

  if (slots.size === 0) {
    return ['AM'];
  }

  return Array.from(slots);
}

/**
 * Visual configuration for grouping protocols by Time of Day.
 */
export const TIME_OF_DAY_GROUPS = [
  {
    key: 'AM',
    title: 'Morning / AM',
    config: {
      dotClass: 'bg-amber-400',
      badgeClass: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
      description: 'Morning wake-up, fasted, or breakfast protocols'
    }
  },
  {
    key: 'NOON',
    title: 'Midday / Noon',
    config: {
      dotClass: 'bg-sky-400',
      badgeClass: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
      description: 'Midday, lunch, or afternoon protocols'
    }
  },
  {
    key: 'BEDTIME',
    title: 'Evening / Bedtime',
    config: {
      dotClass: 'bg-indigo-400',
      badgeClass: 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30',
      description: 'Evening, dinner, or bedtime protocols'
    }
  },
  {
    key: 'MULTI',
    title: 'Split Dosing / Multi-Slot',
    config: {
      dotClass: 'bg-purple-400',
      badgeClass: 'bg-purple-500/15 text-purple-300 border border-purple-500/30',
      description: 'Protocols administered multiple times throughout the day'
    }
  },
  {
    key: 'PRN',
    title: 'Flexible / As Needed (PRN)',
    config: {
      dotClass: 'bg-emerald-400',
      badgeClass: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
      description: 'As-needed, workout-timed, or acute flexible doses'
    }
  },
  {
    key: 'UNSPECIFIED',
    title: 'Unspecified / Other',
    config: {
      dotClass: 'bg-slate-400',
      badgeClass: 'bg-slate-500/15 text-slate-300 border border-slate-500/30',
      description: 'Protocols without a designated time of day'
    }
  }
];

/**
 * Determines the group key ('AM', 'NOON', 'BEDTIME', 'MULTI', 'PRN', 'UNSPECIFIED')
 * for a protocol based on its timingOfDay string.
 */
export function getTimeOfDayGroupKey(timingOfDay = '') {
  const timing = (timingOfDay || '').trim().toLowerCase();
  if (!timing) {
    return 'UNSPECIFIED';
  }

  // Handle explicit PRN / As Needed
  if (
    timing.includes('prn') || 
    timing.includes('as needed') || 
    timing.includes('flexible') || 
    timing.includes('workout')
  ) {
    return 'PRN';
  }

  // Multi-dose patterns
  const isMulti =
    timing.includes('twice') ||
    timing.includes('three times') ||
    timing.includes('bid') ||
    timing.includes('tid') ||
    timing.includes('thrice') ||
    timing.includes('split') ||
    timing.includes('multi') ||
    (timing.includes('am') && (timing.includes('bedtime') || timing.includes('evening') || timing.includes('night') || timing.includes('pm'))) ||
    (timing.includes('morning') && (timing.includes('night') || timing.includes('bedtime') || timing.includes('evening')));

  if (isMulti) {
    return 'MULTI';
  }

  const isAm =
    timing.includes('am') || 
    timing.includes('morning') || 
    timing.includes('wake') || 
    timing.includes('fasted') ||
    timing.includes('breakfast');

  const isNoon =
    timing.includes('noon') || 
    timing.includes('midday') || 
    timing.includes('afternoon') || 
    timing.includes('lunch');

  const isBedtime =
    timing.includes('bedtime') || 
    timing.includes('evening') || 
    timing.includes('pm') || 
    timing.includes('night') || 
    timing.includes('dinner') ||
    timing.includes('sleep');

  const matchCount = (isAm ? 1 : 0) + (isNoon ? 1 : 0) + (isBedtime ? 1 : 0);
  if (matchCount > 1) {
    return 'MULTI';
  }

  if (isAm) return 'AM';
  if (isNoon) return 'NOON';
  if (isBedtime) return 'BEDTIME';

  return 'UNSPECIFIED';
}

/**
 * Determines whether a protocol is scheduled for a given Date.
 */
export function isProtocolScheduledOnDate(protocol, targetDate) {
  if (!protocol || !targetDate) return false;

  const targetDateStr = formatDateIso(targetDate);

  // 1. Boundary check: Start Date
  if (protocol.startDate && targetDateStr < protocol.startDate) {
    return false;
  }

  // 2. Boundary check: End Date
  if (protocol.endDate && targetDateStr > protocol.endDate) {
    return false;
  }

  // 3. Resolve anchor date (protocol.startDate > protocol.createdAt > today)
  const anchorDate = getProtocolAnchorDate(protocol);

  // 4. Parse natural language frequency schedule
  const schedule = parseFrequencySchedule(protocol.frequencySchedule || 'Daily');

  // 5. Evaluate schedule activity on target date
  return isScheduleActiveOnDate(schedule, targetDate, anchorDate);
}

/**
 * Returns all protocols scheduled on a given Date, filtered by status & category,
 * and bucketed into time slots.
 */
export function getScheduleForDate(protocols = [], targetDate, { statusFilter = 'Active', categoryFilter = 'All' } = {}) {
  const todayStr = formatDateIso(new Date());

  const filtered = protocols.filter(protocol => {
    // 1. Status Filter
    const effectiveStatus = calculateEffectiveStatus(protocol, todayStr);
    if (statusFilter !== 'All' && effectiveStatus !== statusFilter) {
      return false;
    }

    // 2. Category Filter
    if (categoryFilter !== 'All') {
      const cat = normalizeCompoundCategory(protocol.category);
      if (cat !== normalizeCompoundCategory(categoryFilter)) return false;
    }

    // 3. Schedule match
    return isProtocolScheduledOnDate(protocol, targetDate);
  });

  // Group into time slots
  const slots = {
    AM: [],
    NOON: [],
    BEDTIME: [],
    PRN: []
  };

  filtered.forEach(p => {
    const pSlots = mapTimingToSlots(p.timingOfDay);
    pSlots.forEach(slotId => {
      if (slots[slotId]) {
        slots[slotId].push(p);
      }
    });
  });

  // Also collect PRN protocols active on this date
  const prnProtocols = protocols.filter(protocol => {
    const effectiveStatus = calculateEffectiveStatus(protocol, todayStr);
    if (statusFilter !== 'All' && effectiveStatus !== statusFilter) return false;
    if (categoryFilter !== 'All') {
      const cat = normalizeCompoundCategory(protocol.category);
      if (cat !== normalizeCompoundCategory(categoryFilter)) return false;
    }
    const schedule = parseFrequencySchedule(protocol.frequencySchedule);
    const timing = (protocol.timingOfDay || '').toLowerCase();
    return schedule.type === 'prn' || timing.includes('prn') || timing.includes('as needed') || timing.includes('flexible') || timing.includes('workout');
  });

  slots.PRN = prnProtocols;

  return {
    date: targetDate,
    dateString: formatDateIso(targetDate),
    totalScheduled: filtered.length,
    protocols: filtered,
    bySlot: slots
  };
}

/**
 * Builds the month matrix (35 or 42 grid cells starting on Sunday or Monday).
 * @param {number} year 
 * @param {number} month (0-indexed, 0 = January)
 * @param {'sun' | 'mon'} weekStart ('sun' for Sun–Sat default, 'mon' for Mon–Sun)
 */
export function getMonthMatrix(year, month, weekStart = 'sun') {
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  // Determine starting index of 1st day of month
  // getDay(): Sun = 0, Mon = 1, Tue = 2, Wed = 3, Thu = 4, Fri = 5, Sat = 6
  let firstDayIndex;
  if (weekStart === 'mon') {
    firstDayIndex = firstDayOfMonth.getDay() - 1;
    if (firstDayIndex === -1) firstDayIndex = 6;
  } else {
    firstDayIndex = firstDayOfMonth.getDay();
  }

  const today = new Date();
  const matrix = [];

  // Previous month padding days
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    const day = prevMonthLastDay - i;
    const date = new Date(year, month - 1, day);
    matrix.push({
      date,
      dateString: formatDateIso(date),
      dayNumber: day,
      isCurrentMonth: false,
      isToday: isSameDay(date, today)
    });
  }

  // Current month days
  const totalDays = lastDayOfMonth.getDate();
  for (let day = 1; day <= totalDays; day++) {
    const date = new Date(year, month, day);
    matrix.push({
      date,
      dateString: formatDateIso(date),
      dayNumber: day,
      isCurrentMonth: true,
      isToday: isSameDay(date, today)
    });
  }

  // Next month padding days to complete full grid (multiples of 7)
  const remaining = (7 - (matrix.length % 7)) % 7;
  for (let day = 1; day <= remaining; day++) {
    const date = new Date(year, month + 1, day);
    matrix.push({
      date,
      dateString: formatDateIso(date),
      dayNumber: day,
      isCurrentMonth: false,
      isToday: isSameDay(date, today)
    });
  }

  return matrix;
}

/**
 * Returns 7 days for the week containing anchorDate.
 * Defaults to starting on Sunday ('sun'), or can start on Monday ('mon').
 * @param {Date | string} anchorDate
 * @param {'sun' | 'mon'} weekStart ('sun' for Sun–Sat default, 'mon' for Mon–Sun)
 */
export function getWeekDays(anchorDate, weekStart = 'sun') {
  const current = new Date(anchorDate);
  let startOffset;
  if (weekStart === 'mon') {
    let dayOfWeek = current.getDay() - 1; // Mon = 0, Sun = 6
    if (dayOfWeek === -1) dayOfWeek = 6;
    startOffset = dayOfWeek;
  } else {
    // Sun = 0, Mon = 1, ..., Sat = 6
    startOffset = current.getDay();
  }

  // Find start day of the week
  const startDate = new Date(current);
  startDate.setDate(current.getDate() - startOffset);

  const today = new Date();
  const days = [];

  const dayNames = weekStart === 'mon'
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fullDayNames = weekStart === 'mon'
    ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    days.push({
      date,
      dateString: formatDateIso(date),
      dayNumber: date.getDate(),
      shortName: dayNames[i],
      fullName: fullDayNames[i],
      isToday: isSameDay(date, today)
    });
  }

  return days;
}

// User Calendar Preferences Persistence (Namespaced per User)
const CALENDAR_PREFS_BASE_KEY = 'supps_peps_calendar_prefs_v1';

export function getCalendarStorageKey(userId) {
  const safeId = (userId || 'guest').replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${CALENDAR_PREFS_BASE_KEY}_${safeId}`;
}

/**
 * Retrieves persisted calendar preferences (weekStart and viewMode).
 * Defaults to weekStart: 'sun' and viewMode: 'month'.
 */
export function getCalendarPreferences(userId) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return { weekStart: 'sun', viewMode: 'month' };
    }
    const key = getCalendarStorageKey(userId);
    let raw = localStorage.getItem(key);
    // If authenticated user does not have preferences yet, check guest preferences
    if (!raw && userId) {
      const guestKey = getCalendarStorageKey(null);
      raw = localStorage.getItem(guestKey);
    }
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        weekStart: parsed.weekStart === 'mon' ? 'mon' : 'sun',
        viewMode: ['month', 'week', 'day'].includes(parsed.viewMode) ? parsed.viewMode : 'month'
      };
    }
  } catch (err) {
    console.warn('Failed to load calendar preferences from localStorage:', err);
  }
  return {
    weekStart: 'sun', // Default: Sun–Sat
    viewMode: 'month'
  };
}

/**
 * Saves updated calendar preferences (weekStart, viewMode) for the user.
 */
export function saveCalendarPreferences(userId, updates = {}) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const current = getCalendarPreferences(userId);
    const merged = {
      ...current,
      ...updates
    };
    const key = getCalendarStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(merged));
    return merged;
  } catch (err) {
    console.warn('Failed to save calendar preferences to localStorage:', err);
  }
}
